/* eslint-disable react-hooks/globals */
import { forwardRef, useEffect, useState } from "react";
import {
    type DragSourceMonitor,
    type DropTargetMonitor,
    useDrag,
    useDrop,
    type XYCoord,
} from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";

import { cn } from "@btnet/utilities-core";

import {
    type IAntGridRowProps,
    type IDragProps,
    RowType,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

import { UnassignedCostGroupId } from "entity/estimate/common/estimate.common.types";

/**
 * Flow chart of the drag and drop hover process:
 *     We need the 3 "global" variables below as the hover event is called by each item being hovered over, not the row being dragged.
 *     It's safe to have these as global, as there can only be one drag action at a time.
 *
 * 1. While an item is being dragged, the "hover" event is being called constantly (~100 times per second)
 * 2. Each call to "hover" is checking if a new item is being hovered over, if it is, we start a move event on a timeout.
 * 2a. If the item being hovered changes, we cancel the previous move event and start a new one.
 * 3. If the timeout is reached, we perform the move event.
 * 3a. If the move event doesn't succeed, we re-trigger the move event on a shorter timeout.
 *     This is to handle the case where the user is hovering over an item, but the position of the mouse has changed.
 * 4. If the dragging ends, we cancel the move event and perform the move synchronously,
 *     otherwise the UI would be out of sync with the server.
 */
let timerId: number = 0;
let lastHoveredIndex: string | number | undefined;
let lastHoveredClientOffset: XYCoord | undefined;

/**
 * must use `React.forwardRef` to create your custom component, and don't forget to use `ref`.
 */
const LineItemDraggableRowInner = forwardRef<HTMLTableRowElement, IAntGridRowProps>(
    (props, ref) => {
        const {
            index,
            moveRow,
            className,
            style,
            canReorder,
            moveRowWithGrouping,
            rowType,
            rowContainerId,
            rowId,
            isInEdit,
            showDefaultDragPreviewImage = true,
            canDrop,
            dragStarted,
            dragEnded,
            debounceHoverEvents,
            ...restProps
        } = props;

        const [isDragging, setIsDragging] = useState(false);
        const [expandedState, setExpandedState] = useState<boolean | undefined>(undefined);
        const [draggedItemId, setDraggedItemId] = useState<number | undefined>(undefined);
        const [startContainerId, setStartContainerId] = useState<string | undefined>(undefined);

        // clientOffset needs passed in here in case the mouse's position changes while the move is happening.
        const handleMove = (item: IDragProps, clientOffset: XYCoord | undefined): boolean => {
            if (!(ref as any).current) {
                return false;
            }

            if (clientOffset === undefined || clientOffset === null) {
                return false;
            }

            if (
                [RowType.Action, RowType.Expand].includes(rowType) ||
                (rowId === UnassignedCostGroupId && item.type === RowType.Group)
            ) {
                // don't allow the action row to be moved, as well as the unassigned group
                return false;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return false;
            }

            if (canDrop && !canDrop(item, props)) {
                return false;
            }

            // Determine rectangle on screen
            const hoverBoundingRect = (ref as any).current?.getBoundingClientRect();

            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

            // Get pixels to the top
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%

            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return false;
            }

            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return false;
            }

            lastHoveredClientOffset = clientOffset;

            // Time to actually perform the action
            if (moveRow) {
                moveRow(dragIndex, hoverIndex);
            } else if (moveRowWithGrouping) {
                moveRowWithGrouping(item, props);
            } else {
                return false;
            }

            // Note: we're mutating the monitor item here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            item.index = hoverIndex;
            return true;
        };

        const debouncedMove = (item: IDragProps, monitor: DropTargetMonitor) => {
            // Determine mouse position
            const clientOffset = monitor.getClientOffset() as XYCoord;
            // Need to store the last hovered position in case the drag ends;
            lastHoveredClientOffset = clientOffset;
            const successfulMove = handleMove(item, clientOffset);
            // If it didn't move, we need to try again in case the user is hovering over the same item but position has changed.
            if (!successfulMove) {
                clearTimeout(timerId);
                timerId = setTimeout(() => {
                    debouncedMove(item, monitor);
                }, 40);
            }
        };

        const [, drop] = useDrop({
            accept:
                rowType === RowType.LineItem
                    ? [RowType.LineItem, RowType.SubGroup]
                    : [RowType.LineItem, RowType.Group, RowType.SubGroup],
            collect: (monitor) => {
                const { index: dragIndex } = monitor.getItem() || {};

                if (dragIndex === index) {
                    return {};
                }
                return {
                    isOver: monitor.isOver(),
                };
            },
            // Hover event triggers constantly, roughly ~100 times per second
            hover: (item: IDragProps, monitor: DropTargetMonitor) => {
                if (!debounceHoverEvents) {
                    // Determine mouse position
                    const clientOffset = monitor.getClientOffset() as XYCoord;
                    handleMove(item, clientOffset);
                    return;
                }

                if (index === lastHoveredIndex) {
                    return;
                }

                lastHoveredIndex = index;

                clearTimeout(timerId);

                timerId = setTimeout(() => {
                    debouncedMove(item, monitor);
                }, 175);
            },
        });

        const handleIsDragging = (monitor: DragSourceMonitor) => {
            const { containerId, id } = monitor.getItem() || {};
            if (id === null || id === undefined) {
                setDraggedItemId(undefined);
                return false;
            }

            // If we don't have a startContainerId but DSM has one,
            // save it off (the begin usually doesn't get it in time)
            if (!startContainerId && containerId) {
                setStartContainerId(containerId);
            }

            // If the dragged row has a container Id, compare it to
            // the rowContainerId -- some line containers use the same rowId
            // so we use rowContainerId for uniqueness
            if (startContainerId && startContainerId !== rowContainerId) {
                return false;
            }

            setDraggedItemId(id);
            return rowId === id;
        };

        const handleDragStart = (monitor: DragSourceMonitor) => {
            const { containerId } = monitor.getItem() || {};
            if (containerId !== undefined) {
                setStartContainerId(containerId);
            }
            setIsDragging(true);
        };

        const handleDragEnd = (
            draggedItem: IDragProps | undefined,
            _monitor: DragSourceMonitor,
        ) => {
            if (debounceHoverEvents) {
                // cancel the hover operation and perform the drag synchronously
                clearTimeout(timerId);
                if (draggedItem && lastHoveredClientOffset) {
                    handleMove(draggedItem, lastHoveredClientOffset);
                }
                lastHoveredIndex = undefined;
                lastHoveredClientOffset = undefined;
            }
            setIsDragging(false);
            setDraggedItemId(undefined);
            setStartContainerId(undefined);

            if (dragEnded && draggedItem && expandedState !== undefined) {
                dragEnded(draggedItem, expandedState);
                setExpandedState(undefined);
            }
        };

        const [collected, drag, dragPreview] = useDrag<IDragProps, never, { isDragging: boolean }>({
            item: {
                type: rowType ?? RowType.LineItem,
                index,
                containerId: rowContainerId,
                id: rowId,
            },
            collect: (monitor) => ({
                isDragging: handleIsDragging(monitor),
            }),
            begin: handleDragStart,
            end: handleDragEnd,
            canDrag: canReorder,
        });

        useEffect(() => {
            if ((ref as any)?.current) {
                drop(drag((ref as any).current));
            }
        }, [drop, drag, ref]);

        useEffect(() => {
            if (
                isDragging &&
                dragStarted &&
                draggedItemId !== undefined &&
                expandedState === undefined
            ) {
                setExpandedState(dragStarted(draggedItemId));
            }
        }, [isDragging, draggedItemId, dragStarted, expandedState, setExpandedState]);

        useEffect(() => {
            if (!showDefaultDragPreviewImage) {
                dragPreview(getEmptyImage());
            }
        }, [dragPreview, showDefaultDragPreviewImage]);

        return (
            <tr
                ref={ref}
                draggable={!isInEdit} // because firefox has issues with inputs inside of a draggable row, we need to make the row undraggable when editing. Dragging with the drag icon will still work
                className={cn(className, {
                    isDragging: collected.isDragging,
                    expandRow: rowType === RowType.Expand,
                })}
                style={style}
                {...restProps}
            />
        );
    },
);

// Only works with Virtual Tables, probably due to only Virtual Tables giving a ref
const LineItemDraggableRow = forwardRef<HTMLTableRowElement, IAntGridRowProps>((props, ref) => {
    const { rowType } = props;
    if (rowType === undefined) {
        // skip rendering the row if this is the proposal format page
        return null;
    }

    return <LineItemDraggableRowInner {...props} ref={ref} />;
});

LineItemDraggableRow.displayName = "LineItemDraggableRow";

export default LineItemDraggableRow;
